package horcrux.frontend.common.views.chat.context

import horcrux.common.api.dtos.types.ContextDocumentChunkAndMetadata
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.views.components.containers.DialogAction
import horcrux.frontend.common.views.components.containers.DialogActionView
import horcrux.frontend.common.views.components.containers.DialogView
import horcrux.frontend.common.views.components.containers.UpdateBanner
import horcrux.frontend.common.views.components.input.InstructionsWithHelp
import horcrux.frontend.common.views.documents.DocumentIcon
import horcrux.frontend.common.withDefaultCSS
import mui.icons.material.Info
import mui.material.Button
import mui.material.ButtonVariant
import mui.material.Dialog
import mui.material.DialogActions
import mui.material.DialogContent
import mui.material.DialogContentText
import mui.material.DialogTitle
import mui.material.SvgIconSize
import mui.system.sx
import react.FC
import react.Props
import react.create
import react.dom.html.ReactHTML.h3
import web.cssom.JustifyContent

external interface ContextDocumentChunkProps : Props {
    var contextDocumentChunkAndMetadata: ContextDocumentChunkAndMetadata
    var currentContextDocumentChunkAndMetadataMaybeState: MaybeState<ContextDocumentChunkAndMetadata>
    var showContextDocumentChunkDetailsDialogState: BooleanState
    var updateBanner: UpdateBanner
    var globalAppState: GlobalAppState
}

val ContextDocumentChunk = FC<ContextDocumentChunkProps> { props ->
    val showDialogState = BooleanState()
    val documentMetadata = props.contextDocumentChunkAndMetadata.documentMetadata
    val title =
        "${documentMetadata.documentName.betterToString()}-${props.contextDocumentChunkAndMetadata.documentChunkIndex.betterToString()}"
    DialogView {
        this.showDialogState = showDialogState
        this.title = title
        this.dialogActionsMaybe = listOf(
            DialogActionView(Info.create {
                this.fontSize = SvgIconSize.medium
            }) {
                props.currentContextDocumentChunkAndMetadataMaybeState.set(props.contextDocumentChunkAndMetadata)
                props.showContextDocumentChunkDetailsDialogState.setTrue()
            },
            DialogAction("OK") { _, _ -> showDialogState.setFalse() }
        )
        DialogTitle { +title }
        DialogContentText {
            +props.contextDocumentChunkAndMetadata.documentChunk.betterToString()
        }
    }
    DocumentIcon {
        this.label = title
        this.documentMetadata = documentMetadata
        this.showDialogState = showDialogState
    }
}

external interface ContextDocumentChunksProps : Props {
    var contextDocumentChunkAndMetadataList: List<ContextDocumentChunkAndMetadata>
    var showSourcesDialogState: BooleanState
    var globalAppState: GlobalAppState
}

val ContextDocumentChunks = FC<ContextDocumentChunksProps> { props ->
    val showContextDocumentChunkDetailsDialogState = BooleanState()
    val currentContextDocumentChunkAndMetadataMaybeState = MaybeState<ContextDocumentChunkAndMetadata>()
    Dialog {
        this.open = props.showSourcesDialogState.get()
        this.onClose = { _, _ -> props.showSourcesDialogState.setFalse() }
        currentContextDocumentChunkAndMetadataMaybeState.get()?.let {
            ContextDocumentChunkDetailsDialog {
                this.showDialogState = showContextDocumentChunkDetailsDialogState
                this.contextDocumentChunkAndMetadata = it
            }
        }

        DialogContent {
            this.withDefaultCSS()
            InstructionsWithHelp {
                this.id = "sources-for-your-answer"
                this.instructions = "Sources For Your Answer"
                this.helpText =
                    """These are the sources used to generate your answer. You can click through them to learn more about why The Grok App answered the way it did."""
                this.textTagSupplierMaybe = { h3 }
            }
            for (contextDocumentAndScore in props.contextDocumentChunkAndMetadataList) {
                ContextDocumentChunk {
                    this.contextDocumentChunkAndMetadata = contextDocumentAndScore
                    this.currentContextDocumentChunkAndMetadataMaybeState =
                        currentContextDocumentChunkAndMetadataMaybeState
                    this.showContextDocumentChunkDetailsDialogState = showContextDocumentChunkDetailsDialogState
                    this.globalAppState = props.globalAppState
                }
            }
        }

        DialogActions {
            this.sx {
                this.justifyContent = JustifyContent.center
            }
            Button {
                +"OK"
                this.variant = ButtonVariant.contained
                this.onClick = { props.showSourcesDialogState.setFalse() }
            }
        }
    }
}
